import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Pencil, Trash2, Star, GripVertical, Camera, Video, Package, Sparkles, PartyPopper, Copy, AlertTriangle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AIPricingTools } from "./AIPricingTools";

interface PackageData {
  id: string;
  name: string;
  price: number;
  description: string | null;
  features: string[];
  is_featured: boolean;
  sort_order: number;
  is_active: boolean;
  category: string | null;
  show_on_homepage: boolean;
}

interface Addon {
  id: string;
  name: string;
  price: number;
  description: string | null;
  sort_order: number;
  is_active: boolean;
  category: string | null;
}

const ADDON_CATEGORIES = [
  { value: "all", label: "All Services", icon: Sparkles },
  { value: "photography", label: "Photography", icon: Camera },
  { value: "cinematography", label: "Cinematography", icon: Video },
  { value: "photobooth", label: "Photo Booth", icon: PartyPopper },
];

const CATEGORIES = [
  { value: "all", label: "All Categories", icon: Package },
  { value: "photography", label: "Photography", icon: Camera },
  { value: "cinematography", label: "Cinematography", icon: Video },
  { value: "bundle", label: "Bundle", icon: Package },
];

export const PricingManager = () => {
  const [packages, setPackages] = useState<PackageData[]>([]);
  const [addons, setAddons] = useState<Addon[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingPackage, setEditingPackage] = useState<PackageData | null>(null);
  const [editingAddon, setEditingAddon] = useState<Addon | null>(null);
  const [isPackageDialogOpen, setIsPackageDialogOpen] = useState(false);
  const [isAddonDialogOpen, setIsAddonDialogOpen] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [addonCategoryFilter, setAddonCategoryFilter] = useState("all");
  const { toast } = useToast();

  // Form states for package
  const [pkgName, setPkgName] = useState("");
  const [pkgPrice, setPkgPrice] = useState("");
  const [pkgDescription, setPkgDescription] = useState("");
  const [pkgFeatures, setPkgFeatures] = useState("");
  const [pkgFeatured, setPkgFeatured] = useState(false);
  const [pkgActive, setPkgActive] = useState(true);
  const [pkgCategory, setPkgCategory] = useState("photography");
  const [pkgShowOnHomepage, setPkgShowOnHomepage] = useState(false);

  // Form states for addon
  const [addonName, setAddonName] = useState("");
  const [addonPrice, setAddonPrice] = useState("");
  const [addonDescription, setAddonDescription] = useState("");
  const [addonActive, setAddonActive] = useState(true);
  const [addonCategory, setAddonCategory] = useState("all");

  useEffect(() => {
    fetchPricing();
  }, []);

  const fetchPricing = async () => {
    setIsLoading(true);
    try {
      const [packagesRes, addonsRes] = await Promise.all([
        supabase
          .from("pricing_packages")
          .select("*")
          .order("sort_order", { ascending: true }),
        supabase
          .from("pricing_addons")
          .select("*")
          .order("sort_order", { ascending: true }),
      ]);

      if (packagesRes.error) throw packagesRes.error;
      if (addonsRes.error) throw addonsRes.error;

      setPackages(packagesRes.data || []);
      setAddons(addonsRes.data || []);
    } catch (error: any) {
      toast({
        title: "Error loading pricing",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const filteredPackages = categoryFilter === "all"
    ? packages
    : packages.filter((pkg) => pkg.category === categoryFilter);

  const filteredAddons = addonCategoryFilter === "all"
    ? addons
    : addons.filter((addon) => addon.category === addonCategoryFilter);

  const openPackageDialog = (pkg?: PackageData) => {
    if (pkg) {
      setEditingPackage(pkg);
      setPkgName(pkg.name);
      setPkgPrice(pkg.price.toString());
      setPkgDescription(pkg.description || "");
      setPkgFeatures(pkg.features.join("\n"));
      setPkgFeatured(pkg.is_featured);
      setPkgActive(pkg.is_active);
      setPkgCategory(pkg.category || "photography");
      setPkgShowOnHomepage(pkg.show_on_homepage || false);
    } else {
      setEditingPackage(null);
      setPkgName("");
      setPkgPrice("");
      setPkgDescription("");
      setPkgFeatures("");
      setPkgFeatured(false);
      setPkgActive(true);
      setPkgCategory("photography");
      setPkgShowOnHomepage(false);
    }
    setIsPackageDialogOpen(true);
  };

  const openAddonDialog = (addon?: Addon) => {
    if (addon) {
      setEditingAddon(addon);
      setAddonName(addon.name);
      setAddonPrice(addon.price.toString());
      setAddonDescription(addon.description || "");
      setAddonActive(addon.is_active);
      setAddonCategory(addon.category || "all");
    } else {
      setEditingAddon(null);
      setAddonName("");
      setAddonPrice("");
      setAddonDescription("");
      setAddonActive(true);
      setAddonCategory("all");
    }
    setIsAddonDialogOpen(true);
  };

  const savePackage = async () => {
    try {
      const data = {
        name: pkgName,
        price: parseFloat(pkgPrice),
        description: pkgDescription || null,
        features: pkgFeatures.split("\n").filter((f) => f.trim()),
        is_featured: pkgFeatured,
        is_active: pkgActive,
        category: pkgCategory,
        sort_order: editingPackage?.sort_order || packages.length + 1,
        show_on_homepage: pkgShowOnHomepage,
      };

      if (editingPackage) {
        const { error } = await supabase
          .from("pricing_packages")
          .update(data)
          .eq("id", editingPackage.id);
        if (error) throw error;
        toast({ title: "Package updated" });
      } else {
        const { error } = await supabase.from("pricing_packages").insert(data);
        if (error) throw error;
        toast({ title: "Package created" });
      }

      setIsPackageDialogOpen(false);
      fetchPricing();
    } catch (error: any) {
      toast({
        title: "Error saving package",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const saveAddon = async () => {
    try {
      const data = {
        name: addonName,
        price: parseFloat(addonPrice),
        description: addonDescription || null,
        is_active: addonActive,
        category: addonCategory,
        sort_order: editingAddon?.sort_order || addons.length + 1,
      };

      if (editingAddon) {
        const { error } = await supabase
          .from("pricing_addons")
          .update(data)
          .eq("id", editingAddon.id);
        if (error) throw error;
        toast({ title: "Add-on updated" });
      } else {
        const { error } = await supabase.from("pricing_addons").insert(data);
        if (error) throw error;
        toast({ title: "Add-on created" });
      }

      setIsAddonDialogOpen(false);
      fetchPricing();
    } catch (error: any) {
      toast({
        title: "Error saving add-on",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const deletePackage = async (id: string) => {
    if (!confirm("Are you sure you want to delete this package?")) return;
    try {
      const { error } = await supabase
        .from("pricing_packages")
        .delete()
        .eq("id", id);
      if (error) throw error;
      toast({ title: "Package deleted" });
      fetchPricing();
    } catch (error: any) {
      toast({
        title: "Error deleting package",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const deleteAddon = async (id: string) => {
    if (!confirm("Are you sure you want to delete this add-on?")) return;
    try {
      const { error } = await supabase
        .from("pricing_addons")
        .delete()
        .eq("id", id);
      if (error) throw error;
      toast({ title: "Add-on deleted" });
      fetchPricing();
    } catch (error: any) {
      toast({
        title: "Error deleting add-on",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const duplicateAddon = async (addon: Addon) => {
    try {
      const data = {
        name: `${addon.name} (Copy)`,
        price: addon.price,
        description: addon.description,
        is_active: addon.is_active,
        category: addon.category,
        sort_order: addons.length + 1,
      };

      const { error } = await supabase.from("pricing_addons").insert(data);
      if (error) throw error;
      
      toast({ title: "Add-on duplicated" });
      fetchPricing();
    } catch (error: any) {
      toast({
        title: "Error duplicating add-on",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
    }).format(price);
  };

  const getCategoryBadge = (category: string | null) => {
    switch (category) {
      case "photography":
        return <Badge variant="outline" className="bg-blue-500/10 text-blue-600 border-blue-200">Photography</Badge>;
      case "cinematography":
        return <Badge variant="outline" className="bg-purple-500/10 text-purple-600 border-purple-200">Cinematography</Badge>;
      case "bundle":
        return <Badge variant="outline" className="bg-green-500/10 text-green-600 border-green-200">Bundle</Badge>;
      default:
        return <Badge variant="secondary">Uncategorized</Badge>;
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-32 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  const homepagePackagesCount = packages.filter(p => p.show_on_homepage).length;

  return (
    <div className="space-y-8">
      {/* Packages Section */}
      <div>
        {homepagePackagesCount > 3 && (
          <Alert variant="destructive" className="mb-4">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              You have {homepagePackagesCount} packages set to display on the homepage. Only the top 3 (by price) will be shown.
            </AlertDescription>
          </Alert>
        )}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
          <h3 className="text-xl font-semibold text-foreground">Packages</h3>
          <div className="flex items-center gap-3">
            {/* Category Filter */}
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by category" />
              </SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((cat) => (
                  <SelectItem key={cat.value} value={cat.value}>
                    <div className="flex items-center gap-2">
                      <cat.icon className="h-4 w-4" />
                      {cat.label}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Dialog open={isPackageDialogOpen} onOpenChange={setIsPackageDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={() => openPackageDialog()} size="sm">
                  <Plus className="mr-2 h-4 w-4" />
                  Add Package
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>
                    {editingPackage ? "Edit Package" : "Add Package"}
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 pt-4">
                  <div>
                    <Label htmlFor="pkg-category">Category</Label>
                    <Select value={pkgCategory} onValueChange={setPkgCategory}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="photography">Photography</SelectItem>
                        <SelectItem value="cinematography">Cinematography</SelectItem>
                        <SelectItem value="bundle">Bundle</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="pkg-name">Name</Label>
                    <Input
                      id="pkg-name"
                      value={pkgName}
                      onChange={(e) => setPkgName(e.target.value)}
                      placeholder="Package name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="pkg-price">Price ($)</Label>
                    <Input
                      id="pkg-price"
                      type="number"
                      value={pkgPrice}
                      onChange={(e) => setPkgPrice(e.target.value)}
                      placeholder="1395"
                    />
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <Label htmlFor="pkg-description">Description</Label>
                      <AIPricingTools
                        name={pkgName}
                        category={pkgCategory}
                        price={pkgPrice}
                        onResult={(desc) => setPkgDescription(desc)}
                      />
                    </div>
                    <Textarea
                      id="pkg-description"
                      value={pkgDescription}
                      onChange={(e) => setPkgDescription(e.target.value)}
                      placeholder="Brief description"
                      rows={2}
                    />
                  </div>
                  <div>
                    <Label htmlFor="pkg-features">Features (one per line)</Label>
                    <Textarea
                      id="pkg-features"
                      value={pkgFeatures}
                      onChange={(e) => setPkgFeatures(e.target.value)}
                      placeholder="8 hours of coverage&#10;800+ edited images&#10;Online gallery"
                      rows={6}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="pkg-featured">Featured Package</Label>
                    <Switch
                      id="pkg-featured"
                      checked={pkgFeatured}
                      onCheckedChange={setPkgFeatured}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="pkg-active">Active</Label>
                    <Switch
                      id="pkg-active"
                      checked={pkgActive}
                      onCheckedChange={setPkgActive}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="pkg-homepage">Show on Main Page</Label>
                    <Switch
                      id="pkg-homepage"
                      checked={pkgShowOnHomepage}
                      onCheckedChange={setPkgShowOnHomepage}
                    />
                  </div>
                  <Button onClick={savePackage} className="w-full">
                    {editingPackage ? "Update Package" : "Create Package"}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        <div className="rounded-xl bg-card border border-border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12"></TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Price</TableHead>
                <TableHead>Features</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Homepage</TableHead>
                <TableHead className="w-24">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredPackages.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                    No packages found {categoryFilter !== "all" && `in ${categoryFilter}`}
                  </TableCell>
                </TableRow>
              ) : (
                filteredPackages.map((pkg) => (
                  <TableRow key={pkg.id}>
                    <TableCell>
                      <GripVertical className="h-4 w-4 text-muted-foreground" />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{pkg.name}</span>
                        {pkg.is_featured && (
                          <Star className="h-4 w-4 fill-primary text-primary" />
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{getCategoryBadge(pkg.category)}</TableCell>
                    <TableCell className="font-semibold text-primary">
                      {formatPrice(pkg.price)}
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-muted-foreground">
                        {pkg.features.length} features
                      </span>
                    </TableCell>
                    <TableCell>
                      <Badge variant={pkg.is_active ? "default" : "secondary"}>
                        {pkg.is_active ? "Active" : "Inactive"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {pkg.show_on_homepage ? (
                        <Badge className="bg-green-500/10 text-green-600 border-green-200">Visible</Badge>
                      ) : (
                        <Badge variant="secondary">Hidden</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openPackageDialog(pkg)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => deletePackage(pkg.id)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Add-ons Section */}
      <div>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
          <h3 className="text-xl font-semibold text-foreground">Add-ons</h3>
          <div className="flex items-center gap-3">
            {/* Category Filter */}
            <Select value={addonCategoryFilter} onValueChange={setAddonCategoryFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by category" />
              </SelectTrigger>
              <SelectContent>
                {ADDON_CATEGORIES.map((cat) => (
                  <SelectItem key={cat.value} value={cat.value}>
                    <div className="flex items-center gap-2">
                      <cat.icon className="h-4 w-4" />
                      {cat.label}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Dialog open={isAddonDialogOpen} onOpenChange={setIsAddonDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={() => openAddonDialog()} size="sm">
                  <Plus className="mr-2 h-4 w-4" />
                  Add Add-on
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>
                    {editingAddon ? "Edit Add-on" : "Add Add-on"}
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 pt-4">
                  <div>
                    <Label htmlFor="addon-category">Service Type</Label>
                    <Select value={addonCategory} onValueChange={setAddonCategory}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select service type" />
                      </SelectTrigger>
                      <SelectContent>
                        {ADDON_CATEGORIES.map((cat) => (
                          <SelectItem key={cat.value} value={cat.value}>
                            <div className="flex items-center gap-2">
                              <cat.icon className="h-4 w-4" />
                              {cat.label}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="addon-name">Name</Label>
                    <Input
                      id="addon-name"
                      value={addonName}
                      onChange={(e) => setAddonName(e.target.value)}
                      placeholder="Add-on name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="addon-price">Price ($)</Label>
                    <Input
                      id="addon-price"
                      type="number"
                      value={addonPrice}
                      onChange={(e) => setAddonPrice(e.target.value)}
                      placeholder="295"
                    />
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <Label htmlFor="addon-description">Description</Label>
                      <AIPricingTools
                        name={addonName}
                        category={addonCategory}
                        price={addonPrice}
                        onResult={(desc) => setAddonDescription(desc)}
                      />
                    </div>
                    <Textarea
                      id="addon-description"
                      value={addonDescription}
                      onChange={(e) => setAddonDescription(e.target.value)}
                      placeholder="Brief description"
                      rows={2}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="addon-active">Active</Label>
                    <Switch
                      id="addon-active"
                      checked={addonActive}
                      onCheckedChange={setAddonActive}
                    />
                  </div>
                  <Button onClick={saveAddon} className="w-full">
                    {editingAddon ? "Update Add-on" : "Create Add-on"}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        <div className="rounded-xl bg-card border border-border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12"></TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Price</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-24">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredAddons.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                    No add-ons found {addonCategoryFilter !== "all" && `in ${ADDON_CATEGORIES.find(c => c.value === addonCategoryFilter)?.label}`}
                  </TableCell>
                </TableRow>
              ) : (
                filteredAddons.map((addon) => {
                  const categoryInfo = ADDON_CATEGORIES.find(c => c.value === addon.category) || ADDON_CATEGORIES[0];
                  return (
                    <TableRow key={addon.id}>
                      <TableCell>
                        <GripVertical className="h-4 w-4 text-muted-foreground" />
                      </TableCell>
                      <TableCell>
                        <span className="font-medium">{addon.name}</span>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={
                          addon.category === "photography" ? "bg-blue-500/10 text-blue-600 border-blue-200" :
                          addon.category === "cinematography" ? "bg-purple-500/10 text-purple-600 border-purple-200" :
                          addon.category === "photobooth" ? "bg-pink-500/10 text-pink-600 border-pink-200" :
                          "bg-muted"
                        }>
                          {categoryInfo.label}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-semibold text-primary">
                        {formatPrice(addon.price)}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground max-w-xs truncate">
                        {addon.description}
                      </TableCell>
                      <TableCell>
                        <Badge variant={addon.is_active ? "default" : "secondary"}>
                          {addon.is_active ? "Active" : "Inactive"}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openAddonDialog(addon)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => duplicateAddon(addon)}
                            title="Duplicate add-on"
                          >
                            <Copy className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => deleteAddon(addon.id)}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
};
